import { useEffect, useState } from "react";
import { collection, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { Trash2 } from "lucide-react";

const EditHackathons = () => {
  const [hackathons, setHackathons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", organizer: "", mode: "Online", prize: "", deadline: "", link: "" });
  const [toast, setToast] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { fetchHackathons(); }, []);

  const fetchHackathons = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "hackathons"));
    setHackathons(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  const handleEdit = (h) => {
    setForm({ title: h.title, organizer: h.organizer, mode: h.mode, prize: h.prize || "", deadline: h.deadline || "", link: h.link || "" });
    setEditId(h.id);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editId) return;
    await updateDoc(doc(db, "hackathons", editId), {
      ...form,
      prize: form.prize ? Number(form.prize) : null,
    });
    setToast("Hackathon updated!");
    setEditId(null);
    fetchHackathons();
    setTimeout(() => setToast(""), 3000);
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "hackathons", id));
    setToast("Hackathon deleted!");
    setDeleteId(null);
    fetchHackathons();
    setTimeout(() => setToast(""), 3000);
  };

  if (loading) return <p className="text-center text-gray-500 py-8">Loading...</p>;

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        All Hackathons ({hackathons.length})
      </h2>

      {editId && (
        <form onSubmit={handleUpdate} className="mb-6 p-4 bg-indigo-50 rounded-xl border border-indigo-100 space-y-3">
          <p className="text-sm font-medium text-indigo-700">Editing hackathon</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input name="title" value={form.title} onChange={handleChange} placeholder="Title" className="input-field" required />
            <input name="organizer" value={form.organizer} onChange={handleChange} placeholder="Organizer" className="input-field" required />
            <select name="mode" value={form.mode} onChange={handleChange} className="filter-select">
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
            </select>
            <input name="prize" value={form.prize} onChange={handleChange} placeholder="Prize" className="input-field" type="number" min="0" />
            <input name="deadline" value={form.deadline} onChange={handleChange} className="input-field" type="date" />
            <input name="link" value={form.link} onChange={handleChange} placeholder="Link" className="input-field" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="button btn-primary text-sm">Save Changes</button>
            <button type="button" onClick={() => setEditId(null)} className="button btn-secondary text-sm">Cancel</button>
          </div>
        </form>
      )}

      {hackathons.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No hackathons to edit.</p>
      ) : (
        <div className="space-y-3">
          {hackathons.map((h) => (
            <div
              key={h.id}
              className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                deleteId === h.id ? "border-red-200 bg-red-50" : "border-gray-100 hover:border-gray-200"
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 truncate">{h.title}</p>
                <p className="text-sm text-gray-500">{h.organizer} &middot; {h.mode}</p>
              </div>

              {deleteId === h.id ? (
                <div className="flex items-center gap-2 ml-4">
                  <span className="text-sm text-red-600 font-medium hidden sm:inline">Delete?</span>
                  <button onClick={() => handleDelete(h.id)} className="button btn-danger text-xs px-3 py-1.5">Confirm</button>
                  <button onClick={() => setDeleteId(null)} className="button btn-secondary text-xs px-3 py-1.5">Cancel</button>
                </div>
              ) : (
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => handleEdit(h)} className="button btn-secondary text-xs px-3 py-1.5">Edit</button>
                  <button onClick={() => setDeleteId(h.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 flex items-center gap-2 bg-white border border-gray-200 shadow-lg rounded-lg px-4 py-3 animate-fade-in">
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm text-gray-700">{toast}</span>
        </div>
      )}
    </div>
  );
};

export default EditHackathons;
